import React, { useState, useEffect } from 'react';
import PoolPerformanceChart from './PoolPerformanceChart';
import TradingActivityFeed from './TradingActivityFeed';
import LeaderboardDisplay from './LeaderboardDisplay';
import TransparencyMetrics from './TransparencyMetrics';
import { usePublicAnalyticsWebSocket } from '../../hooks/usePublicAnalyticsWebSocket';
import './PublicAnalytics.css';

const PublicAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState({
    poolPerformance: [],
    tradingActivity: [],
    leaderboard: [],
    transparencyMetrics: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('30d');
  const [activeTab, setActiveTab] = useState('overview');

  // WebSocket integration for real-time analytics updates
  const {
    analyticsData: wsAnalyticsData,
    isConnected: wsConnected,
    isSubscribed: wsSubscribed,
    lastUpdate: wsLastUpdate,
    poolPerformance: wsPoolPerformance,
    tradingActivity: wsTradingActivity,
    leaderboard: wsLeaderboard,
    transparencyMetrics: wsTransparencyMetrics,
    refreshAnalytics: wsRefreshAnalytics,
    isDataFresh,
    isDataStale
  } = usePublicAnalyticsWebSocket({
    onAnalyticsUpdate: (data) => {
      console.log('📈 Real-time analytics update received:', data);
      
      // Update local state with WebSocket data
      setAnalyticsData(prev => ({
        ...prev,
        ...data
      }));
      
      // If we were loading, we can stop now
      if (loading) {
        setLoading(false);
      }
    },
    onError: (error) => {
      console.error('Public analytics WebSocket error:', error);
      setError('Real-time updates temporarily unavailable');
    },
    autoSubscribe: true,
    updateThrottleMs: 3000 // Throttle updates to prevent too frequent re-renders
  });

  useEffect(() => {
    fetchAnalyticsData();
    // Refresh data every 5 minutes
    const interval = setInterval(fetchAnalyticsData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [timeframe]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all analytics data in parallel
      const [
        poolPerformanceResponse,
        tradingActivityResponse,
        leaderboardResponse,
        transparencyResponse
      ] = await Promise.all([
        fetch(`/api/analytics/pools/performance?timeframe=${timeframe}`),
        fetch('/api/analytics/trading/activity?limit=50'),
        fetch(`/api/analytics/managers/leaderboard?timeframe=${timeframe}`),
        fetch('/api/analytics/transparency/metrics')
      ]);

      // Check if all requests were successful
      if (!poolPerformanceResponse.ok || !tradingActivityResponse.ok || 
          !leaderboardResponse.ok || !transparencyResponse.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const [
        poolPerformanceData,
        tradingActivityData,
        leaderboardData,
        transparencyData
      ] = await Promise.all([
        poolPerformanceResponse.json(),
        tradingActivityResponse.json(),
        leaderboardResponse.json(),
        transparencyResponse.json()
      ]);

      setAnalyticsData({
        poolPerformance: poolPerformanceData.data?.pools || [],
        tradingActivity: tradingActivityData.data?.trades || [],
        leaderboard: leaderboardData.data?.leaderboard || [],
        transparencyMetrics: transparencyData.data || {}
      });

    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setError('Failed to load analytics data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'performance', label: 'Performance', icon: '📈' },
    { id: 'activity', label: 'Trading Activity', icon: '💹' },
    { id: 'leaderboard', label: 'Leaderboard', icon: '🏆' }
  ];

  if (loading && !analyticsData.poolPerformance.length) {
    return (
      <div className="public-analytics loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-analytics error">
        <div className="error-message">
          <h3>⚠️ Error Loading Analytics</h3>
          <p>{error}</p>
          <button onClick={fetchAnalyticsData} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="public-analytics">
      <div className="analytics-header">
        <div className="header-content">
          <h2>📊 Public Investment Analytics</h2>
          <p>Transparent performance data and insights for all investment pools</p>
        </div>
        
        <div className="header-controls">
          <div className="timeframe-selector">
            <label>Timeframe:</label>
            <select 
              value={timeframe} 
              onChange={(e) => handleTimeframeChange(e.target.value)}
              className="timeframe-select"
            >
              <option value="7d">7 Days</option>
              <option value="30d">30 Days</option>
              <option value="90d">90 Days</option>
            </select>
          </div>
          
          <button 
            onClick={fetchAnalyticsData} 
            className="refresh-btn"
            disabled={loading}
          >
            {loading ? '🔄' : '↻'} Refresh
          </button>
        </div>
      </div>

      <div className="analytics-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="analytics-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="overview-grid">
              <div className="overview-section transparency">
                <h3>Platform Overview</h3>
                <TransparencyMetrics data={analyticsData.transparencyMetrics} />
              </div>
              
              <div className="overview-section top-performers">
                <h3>Top Performing Pools</h3>
                <div className="top-pools-preview">
                  {analyticsData.poolPerformance
                    .sort((a, b) => {
                      const aReturn = a.performanceHistory[a.performanceHistory.length - 1]?.cumulativeReturn || 0;
                      const bReturn = b.performanceHistory[b.performanceHistory.length - 1]?.cumulativeReturn || 0;
                      return bReturn - aReturn;
                    })
                    .slice(0, 3)
                    .map(pool => {
                      const latestReturn = pool.performanceHistory[pool.performanceHistory.length - 1]?.cumulativeReturn || 0;
                      return (
                        <div key={pool.id} className="top-pool-item">
                          <div className="pool-info">
                            <span className="pool-name">{pool.name}</span>
                            <span className="pool-strategy">{pool.strategy}</span>
                          </div>
                          <div className={`pool-return ${latestReturn >= 0 ? 'positive' : 'negative'}`}>
                            {latestReturn >= 0 ? '+' : ''}{latestReturn.toFixed(2)}%
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              </div>
              
              <div className="overview-section recent-activity">
                <h3>Recent Activity</h3>
                <div className="activity-preview">
                  {analyticsData.tradingActivity.slice(0, 5).map((trade, index) => (
                    <div key={index} className="activity-item">
                      <div className="trade-info">
                        <span className={`trade-type ${trade.tradeType}`}>
                          {trade.tradeType.toUpperCase()}
                        </span>
                        <span className="trade-amount">
                          {trade.amountIn.toFixed(4)} SOL
                        </span>
                      </div>
                      <div className="trade-time">
                        {new Date(trade.executedAt).toLocaleTimeString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'performance' && (
          <div className="performance-tab">
            <div className="performance-section">
              <h3>Pool Performance Comparison</h3>
              <PoolPerformanceChart 
                data={analyticsData.poolPerformance} 
                timeframe={timeframe}
              />
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="activity-tab">
            <div className="activity-section">
              <h3>Recent Trading Activity</h3>
              <TradingActivityFeed data={analyticsData.tradingActivity} />
            </div>
          </div>
        )}

        {activeTab === 'leaderboard' && (
          <div className="leaderboard-tab">
            <div className="leaderboard-section">
              <h3>Manager Performance Leaderboard</h3>
              <LeaderboardDisplay 
                data={analyticsData.leaderboard} 
                timeframe={timeframe}
              />
            </div>
          </div>
        )}
      </div>

      <div className="analytics-footer">
        <div className="disclaimer">
          <p>
            <strong>Disclaimer:</strong> Past performance does not guarantee future results. 
            All investments carry risk of loss. This data is provided for informational purposes only.
          </p>
        </div>
        
        <div className="last-updated">
          <p>
            Last updated: {new Date(analyticsData.transparencyMetrics.lastUpdated || Date.now()).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicAnalytics;